'use client';
import { css, cx } from '@repo/pandacss/styled-system/css';
import React, { type CSSProperties, useState } from 'react';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';

interface ShowMoreProps {
  children: React.ReactNode;
  instanceId: string;
  isDefaultExpanded?: boolean;
  maxContentHeight?: number;
  showMoreText?: string;
  showLessText?: string;
  hideBottomGradient?: boolean;
  className?: string;
}

export const ShowMore: React.FC<ShowMoreProps> = ({
  children,
  hideBottomGradient = false,
  instanceId,
  isDefaultExpanded = false,
  maxContentHeight = 150,
  showMoreText = 'Show More',
  showLessText = 'Show Less',
  className,
}) => {
  // isExpanded is used to set aria-expanded attribute only
  const [isExpanded, setIsExpanded] = useState(isDefaultExpanded);
  const [isShorterContent, setIsShorterContent] = useState(false);

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsExpanded(e.target.checked);
  };

  return (
    <div className={className}>
      {isShorterContent && (
        // Adding exactly 34px to the bottom margin to reduce layout shift on
        // page render for the "Show More" button
        <div
          id={`${instanceId}-short-content`}
          className={css({
            marginBottom: '[34px]',
            overflowY: 'hidden',
            textAlign: '[inherit]',
            color: '[inherit]',
            position: 'relative',
          })}
        >
          {children}
        </div>
      )}
      {!isShorterContent && (
        <div className={css({ position: 'relative' })}>
          <input
            type="checkbox"
            className="checkbox-toggle"
            id={`checkbox-toggle-${instanceId}`}
            defaultChecked={isDefaultExpanded}
            hidden
            onChange={handleToggle}
          />
          <div
            className={cx(
              'show-more-content',
              css({
                overflowY: 'hidden',
                textAlign: '[inherit]',
                color: '[inherit]',
                position: 'relative',
                marginBottom: {
                  base: '4',
                  md: 'var(--margin-bottom-content-desktop)',
                },
                _after:
                  hideBottomGradient || isExpanded
                    ? {
                        display: 'none',
                      }
                    : {
                        display: 'block',
                        position: 'absolute',
                        width: 'full',
                        backgroundImage: '[linear-gradient(hsla(0,0%,100%,0),white)]',
                        bottom: '0',
                        height: '[50%]',
                        content: '""',
                      },
              }),
            )}
            style={
              {
                maxHeight: isExpanded ? '100%' : `${maxContentHeight}px`,
                '--margin-bottom-content-desktop': '1rem',
              } as CSSProperties
            }
            id={`show-more-content-${instanceId}`}
            ref={(ref: HTMLDivElement | null) => {
              const scrollHeight = ref?.scrollHeight;
              if (scrollHeight && scrollHeight < maxContentHeight) {
                setIsShorterContent(true);
              }
            }}
          >
            {children}
          </div>
          <label
            htmlFor={`checkbox-toggle-${instanceId}`}
            role="button"
            aria-controls={`show-more-content-${instanceId}`}
            aria-expanded={isExpanded}
            tabIndex={0}
            className={css({
              display: 'block',
              maxWidth: 'fit',
            })}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                document?.getElementById(`checkbox-toggle-${instanceId}`)?.click();
              }
            }}
          >
            {!isExpanded ? (
              <span
                className={css({
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  color: 'blue.main',
                  alignItems: 'center',
                  display: 'inline-flex',
                  maxWidth: '[inherit]',
                  columnGap: '1',
                  rowGap: '1',
                  textStyle: 'textSmall',
                  _hover: {
                    textDecorationLine: 'underline',
                    textDecorationColor: 'blue.main',
                  },
                })}
              >
                {showMoreText}
                <MdKeyboardArrowDown />
              </span>
            ) : (
              <span
                className={css({
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  color: 'blue.main',
                  alignItems: 'center',
                  display: 'inline-flex',
                  maxWidth: '[inherit]',
                  columnGap: '1',
                  rowGap: '1',
                  textStyle: 'textSmall',
                  _hover: {
                    textDecorationLine: 'underline',
                    textDecorationColor: 'blue.main',
                  },
                })}
              >
                {showLessText}
                <MdKeyboardArrowUp />
              </span>
            )}
          </label>
        </div>
      )}
    </div>
  );
};
